import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowRight, WarningCircle } from "@phosphor-icons/react";
import { api, saveAuth, errMsg } from "@/lib/api";
import AuthSplitScreen from "@/components/AuthSplitScreen";
import ModernInput from "@/components/ModernInput";
import MagneticButton from "@/components/MagneticButton";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", {
        email: email.trim().toLowerCase(),
        password,
      });
      if (!data?.token || !data?.user) throw new Error("Resposta inválida do servidor");
      saveAuth(data.token, data.user);
      toast.success(`Bem-vindo, ${data.user.name}`);
      nav(data.user.role === "admin" ? "/app/admin" : "/app", { replace: true });
    } catch (err) {
      const msg = errMsg(err, "Erro ao entrar");
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthSplitScreen side="right">
      <motion.div
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
      >
        <div className="label-caps mb-3">Bem-vindo de volta</div>
        <h1 className="text-fluid-h2 font-display font-black text-white mb-3 wrap-safe">
          Entre no seu <span className="text-[var(--accent)]">banco</span>.
        </h1>
        <p className="text-white/60 mb-8 wrap-safe text-fluid-lead">
          Acesse seu painel, gerencie taxas, dispare campanhas e veja seu saldo em tempo real.
        </p>

        <form onSubmit={submit} className="space-y-4" noValidate data-testid="login-form">
          <ModernInput
            label="E-mail"
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (error) setError("");
            }}
            testid="login-email"
          />

          <ModernInput
            label="Senha"
            type="password"
            required
            autoComplete="current-password"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              if (error) setError("");
            }}
            testid="login-password"
          />

          {error && (
            <motion.div
              initial={{ opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
              className="flex items-start gap-2 border border-[var(--danger)]/40 bg-[var(--danger)]/10 text-[var(--danger)] rounded-xl px-4 py-3 text-sm"
              data-testid="login-error"
            >
              <WarningCircle size={16} weight="fill" className="mt-0.5 shrink-0" />
              <span className="wrap-safe">{error}</span>
            </motion.div>
          )}

          <MagneticButton
            as="button"
            type="submit"
            disabled={loading || !email || !password}
            className="btn-magnetic w-full mt-2"
            testid="login-submit"
            style={{
              opacity: loading || !email || !password ? 0.6 : 1,
              cursor: loading || !email || !password ? "not-allowed" : "pointer",
            }}
          >
            {loading ? (
              <>
                <span className="inline-block w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                Entrando...
              </>
            ) : (
              <>
                Entrar na conta
                <ArrowRight size={16} weight="bold" />
              </>
            )}
          </MagneticButton>
        </form>

        <div className="mt-8 pt-6 border-t border-white/5 flex items-center justify-between gap-4 text-sm flex-wrap">
          <span className="text-white/50">Ainda não tem conta?</span>
          <Link
            to="/register"
            className="text-[var(--accent)] font-bold hover:underline flex items-center gap-1"
            data-testid="login-to-register"
          >
            Abrir conta grátis <ArrowRight size={14} weight="bold" />
          </Link>
        </div>
      </motion.div>
    </AuthSplitScreen>
  );
}
